import { useCallback, useEffect } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../config/auth';
import { useAuthStore } from '../store/authStore';

export function useAuth() {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || null);
  const { setAccount, setAccessToken, logout: storeLogout } = useAuthStore();

  useEffect(() => {
    if (account) {
      setAccount(account);
    }
  }, [account, setAccount]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      setAccessToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await instance.acquireTokenPopup(loginRequest);
          setAccessToken(response.accessToken);
          return response.accessToken;
        } catch (popupError) {
          console.error('Failed to acquire token via popup:', popupError);
          return null;
        }
      }
      console.error('Failed to acquire token silently:', error);
      return null;
    }
  }, [account, instance, setAccessToken]);

  const login = useCallback(async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      storeLogout();
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance, storeLogout]);

  return {
    account,
    isAuthenticated: !!account,
    login,
    logout,
    getAccessToken,
  };
}
